// components/customers/QuickLockDialog.tsx
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useQuery } from '@tanstack/react-query'
import { zodResolver } from '@hookform/resolvers/zod'
import { Search, Plus, Loader2, User, UserCheck, Clock, AlertCircle, CheckCircle, Crown, Ban, Phone, MessageCircle } from 'lucide-react'
import { useCheckContact, useCreateCustomer } from '@/hooks/useCustomers'
import { createCustomerSchema, type CreateCustomerData } from '@/schemas/customer'
import { customerCategoriesApi } from '@/api/customer-categories'
import { CUSTOMER_CATEGORY_TYPES, VIP_LEVEL_OPTIONS } from '@/types/customer-category'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import type { CheckContactResponse, CreateCustomerRequest } from '@/types/customer'
import { showError } from '@/lib/error'

interface QuickLockDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function QuickLockDialog({ open, onOpenChange }: QuickLockDialogProps) {
  const [step, setStep] = useState<'search' | 'exists' | 'create'>('search')
  const [checkResult, setCheckResult] = useState<CheckContactResponse | null>(null)

  const checkMutation = useCheckContact()
  const createMutation = useCreateCustomer()

  // 获取客户类型选项
  const { data: categoryOptions = [] } = useQuery({
    queryKey: ['customer-categories', 'options'],
    queryFn: customerCategoriesApi.options,
    enabled: open,
  })

  // 查询表单
  const searchForm = useForm<{ value: string }>({
    defaultValues: { value: '' },
  })

  // 创建表单
  const createForm = useForm<CreateCustomerData>({
    resolver: zodResolver(createCustomerSchema),
    defaultValues: {
      name: '',
      gender: '0',
      province: '',
      city: '',
      district: '',
      address: '',
      remark: '',
      category_id: undefined,
      vip_level: 99,
      contacts: [
        { type: 1, key: 'mobile_1', value: '' },
        { type: 2, key: 'wechat_1', value: '' },
      ],
    },
  })

  // 监听选中的客户类型，判断是否显示 VIP 等级
  const selectedCategoryId = createForm.watch('category_id')
  const selectedCategory = categoryOptions.find(c => c.id === selectedCategoryId)
  const showVipLevel = !!selectedCategory && selectedCategory.type !== 0

  // 预查询 — 仅传 value，后端同时查微信和手机
  const handleSearch = async (data: { value: string }) => {
    const value = data.value.trim()
    if (!value) return

    try {
      const result = await checkMutation.mutateAsync({ value })
      setCheckResult(result)

      if (result.exists) {
        setStep('exists')
      } else {
        // 未锁定，进入创建流程
        // 预填搜索值：纯数字默认填手机号，否则填微信号
        const isNumeric = /^\d+$/.test(value)
        setStep('create')
        createForm.reset({
          name: '',
          gender: '0',
          province: '',
          city: '',
          district: '',
          address: '',
          remark: '',
          category_id: undefined,
          vip_level: 99,
          contacts: [
            { type: 1 as const, key: 'mobile_1', value: isNumeric ? value : '' },
            { type: 2 as const, key: 'wechat_1', value: isNumeric ? '' : value },
          ],
        })
      }
    } catch (error) {
      showError(error, '预查锁定失败')
    }
  }

  // 创建客户
  const handleCreate = async (data: CreateCustomerData) => {
    try {
      const submitData: CreateCustomerRequest = {
        ...data,
        gender: data.gender ? (Number(data.gender) as 0 | 1 | 2) : undefined,
      }
      await createMutation.mutateAsync(submitData)
      handleClose()
    } catch (error) {
      showError(error, '创建客户失败')
    }
  }

  // 重置状态
  const handleClose = () => {
    setStep('search')
    setCheckResult(null)
    searchForm.reset()
    createForm.reset()
    onOpenChange(false)
  }

  // 返回搜索
  const handleBack = () => {
    setStep('search')
    setCheckResult(null)
  }

  // 判断是否 VIP（vip_level < 99 视为 VIP）
  const isVip = (level?: number) => level !== undefined && level < 99

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Search className="h-5 w-5" />
            预查锁定
          </DialogTitle>
          <DialogDescription>
            输入手机号或微信号查询是否已被锁定
          </DialogDescription>
        </DialogHeader>

        {/* 步骤 1: 搜索 */}
        {step === 'search' && (
          <Form {...searchForm}>
            <form onSubmit={searchForm.handleSubmit(handleSearch)} className="space-y-4">
              <FormField
                control={searchForm.control}
                name="value"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        placeholder="输入手机号或微信号"
                        {...field}
                        className="h-12 text-lg"
                        autoFocus
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button
                type="submit"
                className="w-full"
                disabled={checkMutation.isPending}
              >
                {checkMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Search className="mr-2 h-4 w-4" />
                )}
                预查询
              </Button>
            </form>
          </Form>
        )}

        {/* 步骤 2: 已存在 */}
        {step === 'exists' && checkResult?.customer && (
          <div className="space-y-4">
            {/* VIP 显眼警告 */}
            {isVip(checkResult.customer.vip_level) && (
              <Alert className="border-amber-500 bg-amber-50 dark:bg-amber-950">
                <Crown className="h-5 w-5 text-amber-600" />
                <AlertDescription className="text-base font-bold text-amber-700 dark:text-amber-400">
                  ⚠️ 该客户为 VIP 客户（V{checkResult.customer.vip_level}），请谨慎操作！
                </AlertDescription>
              </Alert>
            )}

            {/* 黑名单显眼警告 */}
            {checkResult.customer.blacklist && (
              <Alert variant="destructive" className="border-red-600 bg-red-50 dark:bg-red-950">
                <Ban className="h-5 w-5 text-red-600" />
                <AlertDescription className="text-base font-bold text-red-700 dark:text-red-400">
                  🚫 该客户为黑名单客户，禁止录单！
                </AlertDescription>
              </Alert>
            )}

            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                该联系方式已被锁定
              </AlertDescription>
            </Alert>

            <div className="rounded-lg border p-4 space-y-3">
              <div className="flex items-center gap-2 flex-wrap">
                <User className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">{checkResult.customer.name}</span>
                <Badge variant="outline">ID: {checkResult.customer.id}</Badge>
                {isVip(checkResult.customer.vip_level) && (
                  <Badge className="bg-amber-500 hover:bg-amber-500 text-white">
                    <Crown className="h-3 w-3 mr-1" />
                    VIP{checkResult.customer.vip_level}
                  </Badge>
                )}
                {checkResult.customer.blacklist && (
                  <Badge variant="destructive">
                    <Ban className="h-3 w-3 mr-1" />
                    黑名单
                  </Badge>
                )}
                {checkResult.customer.category_name && (
                  <Badge variant="secondary">{checkResult.customer.category_name}</Badge>
                )}
              </div>
              <Separator />
              {/* 锁定人 */}
              <div className="flex items-center gap-2 text-sm">
                <UserCheck className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">锁定人:</span>
                <span className="font-medium">{checkResult.customer.locked_by_name || '-'}</span>
              </div>
              {/* 锁定时间 */}
              <div className="flex items-center gap-2 text-sm">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">锁定时间:</span>
                <span className="font-medium">
                  {checkResult.customer.locked_at
                    ? new Date(checkResult.customer.locked_at).toLocaleString('zh-CN')
                    : '-'}
                </span>
              </div>
            </div>

            <Button variant="outline" onClick={handleBack} className="w-full">
              返回查询
            </Button>
          </div>
        )}

        {/* 步骤 3: 创建 */}
        {step === 'create' && (
          <div className="space-y-4">
            <Alert className="border-green-500 text-green-700 bg-green-50 dark:bg-green-950">
              <CheckCircle className="h-4 w-4 text-green-500" />
              <AlertDescription>
                该联系方式未被锁定，可以录入
              </AlertDescription>
            </Alert>

            <Form {...createForm}>
              <form onSubmit={createForm.handleSubmit(handleCreate)} className="space-y-4">
                {/* contacts 级别的验证错误（手机号/微信号必填） */}
                {createForm.formState.errors.contacts?.message && (
                  <p className="text-sm font-medium text-destructive">
                    {String(createForm.formState.errors.contacts.message)}
                  </p>
                )}
                {/* 手机号 — 必填 */}
                <FormField
                  control={createForm.control}
                  name="contacts.0.value"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-1.5">
                        <Phone className="h-3.5 w-3.5" />
                        手机号 <span className="text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input placeholder="请输入手机号" {...field} autoFocus />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* 微信号 — 必填 */}
                <FormField
                  control={createForm.control}
                  name="contacts.1.value"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-1.5">
                        <MessageCircle className="h-3.5 w-3.5" />
                        微信号 <span className="text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input placeholder="请输入微信号" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={createForm.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>姓名 <span className="text-destructive">*</span></FormLabel>
                      <FormControl>
                        <Input placeholder="请输入客户姓名" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* 客户类型 — 必填 */}
                <FormField
                  control={createForm.control}
                  name="category_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>客户类型 <span className="text-destructive">*</span></FormLabel>
                      <FormControl>
                        <select
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                          value={field.value ?? ''}
                          onChange={(event) => {
                            const nextValue = event.target.value ? Number(event.target.value) : undefined
                            const nextCategory = categoryOptions.find(c => c.id === nextValue)
                            field.onChange(nextValue)
                            // 普通客户 → vip_level=99；VIP 类型 → 重置为默认值
                            createForm.setValue(
                              'vip_level',
                              nextCategory && nextCategory.type !== 0 ? 0 : 99,
                            )
                          }}
                        >
                          <option value="">请选择客户类型</option>
                          {categoryOptions.map(category => {
                            const typeLabel = CUSTOMER_CATEGORY_TYPES.find(t => t.value === category.type)?.label
                            return (
                              <option key={category.id} value={category.id}>
                                {category.name}{typeLabel ? `（${typeLabel}）` : ''}
                              </option>
                            )
                          })}
                        </select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* VIP 等级 — 仅 VIP 类型客户显示 */}
                {showVipLevel && (
                  <FormField
                    control={createForm.control}
                    name="vip_level"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center gap-1.5">
                          <Crown className="h-3.5 w-3.5 text-amber-500" />
                          VIP 等级 <span className="text-destructive">*</span>
                        </FormLabel>
                        <FormControl>
                          <select
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                            value={field.value ?? ''}
                            onChange={(event) => field.onChange(Number(event.target.value))}
                          >
                            <option value="">请选择 VIP 等级</option>
                            {VIP_LEVEL_OPTIONS.map(option => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                <FormField
                  control={createForm.control}
                  name="gender"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>性别</FormLabel>
                      <FormControl>
                        <select
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                          value={field.value}
                          onChange={field.onChange}
                        >
                          <option value={0}>未知</option>
                          <option value={1}>男</option>
                          <option value={2}>女</option>
                        </select>
                      </FormControl>
                    </FormItem>
                  )}
                />

                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={handleBack} className="flex-1">
                    取消
                  </Button>
                  <Button type="submit" disabled={createMutation.isPending} className="flex-1">
                    {createMutation.isPending ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Plus className="mr-2 h-4 w-4" />
                    )}
                    确认锁定
                  </Button>
                </div>
              </form>
            </Form>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
